/**
 * DateField — a date you can type ("10/22", "Oct 22", "2026-10-22") or pick
 * from a calendar. The value is a `YYYY-MM-DD` string, the same as the browser
 * date input it replaces, so the form behind it does not change.
 */
import { CalendarPopover } from './CalendarPopover';
import { formatDateLabel, parseDateInputFrom } from './date-time-input';
import { fieldStyle } from './field-style';
import { usePickerField } from './use-picker-field';

export interface DateFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** The field's accessible name. */
  ariaLabel: string;
  /** Earliest pickable date, `YYYY-MM-DD`. Typed dates are not refused here —
   *  the form's own rules say why a date is not allowed. */
  min?: string;
  error?: boolean;
  placeholder?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

const CALENDAR_BUTTON: React.CSSProperties = {
  position: 'absolute',
  right: 4,
  top: 4,
  height: 24,
  padding: '0 6px',
  border: 'none',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--nb-subtle)',
  cursor: 'pointer',
  fontSize: 13,
};

export function DateField({
  value,
  onChange,
  ariaLabel,
  min,
  error,
  placeholder = 'e.g. Oct 22',
  ...aria
}: DateFieldProps) {
  const field = usePickerField({
    value,
    onChange,
    parse: (text) =>
      parseDateInputFrom(
        text,
        Number((value || min || '').slice(0, 4)) || new Date().getFullYear(),
        min,
      ),
    label: formatDateLabel,
  });
  return (
    <div ref={field.wrapperRef} onBlur={field.onBlur} style={{ position: 'relative' }}>
      <input
        type="text"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={field.open}
        autoComplete="off"
        placeholder={placeholder}
        value={field.display}
        onFocus={() => field.setOpen(true)}
        onClick={() => field.setOpen(true)}
        onChange={(e) => field.onInputChange(e.target.value)}
        onKeyDown={field.onKeyDown}
        style={fieldStyle(error ? 'error' : undefined, { paddingRight: 34 })}
        {...aria}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={`Open calendar for ${ariaLabel}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => field.setOpen(!field.open)}
        style={CALENDAR_BUTTON}
      >
        ▾
      </button>
      {field.open && <CalendarPopover value={value} min={min} onPick={field.choose} />}
    </div>
  );
}
